import type { Iteration } from '@rhythmic/db/types'
import type { UpdateIterationPayload } from '@rhythmic/types'
import { UpdateIterationSchema } from '@rhythmic/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getIterationsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:updateIteration')

export interface UpdateIterationContext {
  teamId: string
}

function validateParams(id: string | undefined, payload: UpdateIterationPayload, context: UpdateIterationContext) {
  const { teamId } = context
  logger.debug('Validating parameters for updateIteration', {
    teamId,
    id,
    payload,
  })
  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to update iteration: missing team identifier')
    throw error
  }
  const parsedChanges = UpdateIterationSchema.parse(payload)

  const applicable = Object.fromEntries(
    Object.entries(parsedChanges).filter(([, value]) => value !== undefined),
  ) as Partial<UpdateIterationPayload>

  if (Object.keys(applicable).length === 0) {
    const error = new Error('No changes provided to update iteration')
    logger.warn('Iteration update aborted: no changes detected', {
      teamId,
      id,
    })
    throw error
  }

  return {
    teamId,
    changes: applicable,
  }
}

export function handleUpdateIteration(
  id: string,
  payload: UpdateIterationPayload,
  context: UpdateIterationContext,
): Transaction | undefined {
  try {
    logger.debug('handleUpdateIteration called', { id, payload, context })
    const { teamId, changes } = validateParams(id, payload, context)
    const collection: Collection<Iteration> = getIterationsCollection(teamId)

    const result = collection.update(id, (draft: Iteration) => {
      Object.assign(draft, changes)
    })

    logger.info('Iteration updated via collection', { teamId, id })

    return result
  } catch (error) {
    logger.error('updateIteration failed', { payload, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update iteration')
  }
}
